/**
 * Query keys do domínio students (apps/crm).
 *
 * `viewScope` participa da key porque a lista de alunos muda entre
 * `'club'` (apenas o clube atual) e `'network'` (multi-clube via
 * `networkClubIds`) — sem isso, alternar o toggle não invalida.
 */
import { tuple } from "./core";

const root = ["students"] as const;

export type StudentViewScope = "club" | "network";

export const students = {
  all: root,
  /** Lista de alunos do clube/rede atual. */
  list: (
    clubId: string | null | undefined,
    viewScope: StudentViewScope | { type: StudentViewScope } | null | undefined,
  ) => tuple(root, "list", clubId, viewScope),
  byId: (studentId: string | null | undefined) => tuple(root, "detail", studentId),
  /** Faturas mensais de um aluno (`student_monthly_billing`). */
  billings: (studentId: string | null | undefined) =>
    tuple(root, "billings", studentId),
  /**
   * TODAS as cobranças de um aluno: as mensalidades de
   * `student_monthly_billing` unidas às faturas de `student_invoices`
   * (evento, locação, fidelidade e aula avulsa sem plano), com dedupe por
   * `student_invoices.monthly_billing_id`.
   *
   * Key própria, e não `billings`, porque a leitura é OUTRA (duas tabelas) —
   * reaproveitar a key faria uma das duas telas ler o cache da outra e perder
   * metade das linhas. Fica sob o root `students` de propósito: markPaid de
   * fatura, baixa de mensalidade e reemissão já invalidam `qk.students.all`,
   * e o prefix-match cobre esta key sem que ninguém precise lembrar dela.
   */
  charges: (studentId: string | null | undefined) =>
    tuple(root, "charges", studentId),
  /** Jornada (lessons + journey_status) de um aluno. */
  journey: (studentId: string | null | undefined) =>
    tuple(root, "journey", studentId),
  /**
   * Aulas em que o aluno esta matriculado (join club_lesson_students x
   * club_lessons x teacher), com filtros opcionais por periodo/status.
   * Wave 3 — Alunos UX: tab "Aulas" no detalhe do aluno.
   */
  lessons: (
    studentId: string | null | undefined,
    filters?: Record<string, unknown>,
  ) => tuple(root, "lessons", studentId, filters ?? {}),
  /**
   * Pacotes de créditos (student_packages) de um aluno — FASE P3 (PPS para
   * Professores). Historico completo, ordenado por purchased_at DESC.
   */
  packages: (studentId: string | null | undefined) =>
    tuple(root, "packages", studentId),
  /**
   * Linha do tempo de evolução (student_progress_entries) de um aluno —
   * FASE P5.1 (PPS para Professores).
   */
  progress: (studentId: string | null | undefined) =>
    tuple(root, "progress", studentId),
} as const;
